/**
 * Searching, sorting and filtering the public texts. The filters open in a
 * sheet on phones (ui/Sheet), and in a popover under their button elsewhere
 * (ui/Popover).
 */
import { effect } from "@preact/signals";
import * as preact from "preact";
import * as _ from "underscore";

import * as deviceDetect from "deviceDetect";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import SearchQueryStore from "stores/SearchQueryStore";
import stores from "stores/stores";
import type { PublicBooksFilter } from "stores/UserStore";
import Button from "ui/Button";
import Chips from "ui/Chips";
import Field from "ui/Field";
import Popover from "ui/Popover";
import Select from "ui/Select";
import Sheet, { showSheet } from "ui/Sheet";
import SearchView from "views/SearchView";

const filter = () => stores.user.state.value.publicBooksFilter;

/** Snap to A1, B1, C1 */
const difficultyFrom = () => {
  const rawValue = filter()?.metrics_CEF?.split("-")[0] ?? "";
  return _.includes(["A", "B", "C"], rawValue[0]) ? `${rawValue[0]}1` : "A1";
};

/** Snap to A2, B2, C2 */
const difficultyTo = () => {
  const rawValue = filter()?.metrics_CEF?.split("-")[1] ?? "";
  return _.includes(["A", "B", "C"], rawValue[0]) ? `${rawValue[0]}2` : "C2";
};

/** Snap to "0", "1000", "10000" */
const lengthFrom = () => {
  const rawValue = filter()?.metrics_words?.split("-")[0];
  switch (rawValue) {
    case "0":
    case "1000":
    case "10000":
      return rawValue;
    case "3000":
      return "1000";
    case "30000":
      return "10000";
  }
  return "0";
};

/** Snap to "1000", "10000", "Infinity" */
const lengthTo = () => {
  const rawValue = filter()?.metrics_words?.split("-")[1];
  switch (rawValue) {
    case "1000":
    case "10000":
    case "Infinity":
      return rawValue;
    case "300":
      return "1000";
    case "3000":
      return "10000";
  }
  return "Infinity";
};

const includeAI = () => filter()?.includeAI ?? true;
const likedOnly = () => filter()?.likedOnly ?? false;
const videoOnly = () => filter()?.videoOnly ?? false;
const includeCompleted = () => filter()?.includeCompleted ?? true;

/** The categories picked: none for all of them */
const categories = () =>
  _.without(stores.user.state.value.publicBooksCategories ?? [], "All");

/**
 * Write the filter back, snapping every value to its canonical form so a
 * legacy or hand-edited setting is normalized as soon as anything changes.
 */
const saveFilter = (patch: Partial<PublicBooksFilter>) => {
  stores.user.save({
    publicBooksFilter: {
      includeAI: includeAI(),
      metrics_CEF: `${difficultyFrom()}-${difficultyTo()}`,
      metrics_words: `${lengthFrom()}-${lengthTo()}`,
      likedOnly: likedOnly(),
      videoOnly: videoOnly(),
      includeCompleted: includeCompleted(),
      ...patch,
    },
  });
};

const numberOfFiltersSet = () =>
  [
    `${difficultyFrom()}-${difficultyTo()}` !== "A1-C2",
    `${lengthFrom()}-${lengthTo()}` !== "0-Infinity",
    categories().length > 0,
    !includeAI(),
    likedOnly(),
    videoOnly(),
    !includeCompleted(),
  ].filter(Boolean).length;

const resetAllFilters = () => {
  stores.user.save({
    publicSortBooksBy: "hotness",
    publicBooksFilter: {
      includeAI: true,
      metrics_CEF: "A1-C2",
      metrics_words: "0-Infinity",
      likedOnly: false,
      videoOnly: false,
      includeCompleted: true,
    },
    publicBooksCategories: [],
  });
};

/** Re-renders `component` as the filters (or the language) change */
const watchFilters = (component: preact.Component) =>
  stores.user.onChange(
    (attributes) => [
      attributes.publicSortBooksBy,
      attributes.publicBooksFilter,
      attributes.publicBooksCategories,
      attributes.currentLearningLanguage,
    ],
    () => component.forceUpdate()
  );

/** A component which re-renders as the filters change */
abstract class FiltersComponent<
  Props = object,
  State = object,
> extends preact.Component<Props, State> {
  private disposeWatch?: () => void;

  componentDidMount() {
    this.disposeWatch = watchFilters(this);
  }

  componentWillUnmount() {
    this.disposeWatch?.();
  }
}

/** The filters' fields, in the sheet or the panel */
class LibraryFilterFields extends FiltersComponent<{
  size: "medium" | "large";
}> {
  // Each end of a range keeps the other on its side of it
  private changeDifficultyFrom = (from: string) => {
    const to =
      from === "C1"
        ? "C2"
        : from === "B1" && difficultyTo() === "A2"
          ? "B2"
          : difficultyTo();
    saveFilter({ metrics_CEF: `${from}-${to}` });
  };

  private changeDifficultyTo = (to: string) => {
    const from =
      to === "A2"
        ? "A1"
        : to === "B2" && difficultyFrom() === "C1"
          ? "B1"
          : difficultyFrom();
    saveFilter({ metrics_CEF: `${from}-${to}` });
  };

  private changeLengthFrom = (from: string) => {
    const to =
      from === "10000"
        ? "Infinity"
        : from === "1000" && lengthTo() === "1000"
          ? "10000"
          : lengthTo();
    saveFilter({ metrics_words: `${from}-${to}` });
  };

  private changeLengthTo = (to: string) => {
    const from =
      to === "1000"
        ? "0"
        : to === "10000" && lengthFrom() === "10000"
          ? "1000"
          : lengthFrom();
    saveFilter({ metrics_words: `${from}-${to}` });
  };

  /** All, or any of the others (all of them being All) */
  private toggleCategory = (category: string, picked: boolean) => {
    const picks =
      category === "All"
        ? []
        : picked
          ? [...categories(), category]
          : _.without(categories(), category);
    stores.user.save({
      publicBooksCategories:
        picks.length === 0 || picks.length === bookUtils.categories.length
          ? ["All"]
          : picks,
    });
  };

  render() {
    const { size } = this.props;
    const lengthUnit = languageMap.usesCharacterCount(
      stores.user.currentLearningLanguage.value
    )
      ? "characters"
      : "words";
    const picked = categories();
    // Anonymous visitors read in local mode, where votes and read status are
    // never sent to the server, so only a real account can have likes or
    // completed texts
    const hasAccount =
      !stores.user.isNew.value && !stores.user.isLocalMode.value;

    return (
      <>
        <Field label="Difficulty">
          <div class="fieldRange">
            <Select
              aria-label="From"
              size={size}
              options={[
                { value: "A1", label: "Beginner" },
                { value: "B1", label: "Intermediate" },
                { value: "C1", label: "Advanced" },
              ]}
              value={difficultyFrom()}
              onChange={this.changeDifficultyFrom}
            />
            to
            <Select
              aria-label="To"
              size={size}
              options={[
                { value: "A2", label: "Beginner" },
                { value: "B2", label: "Intermediate" },
                { value: "C2", label: "Advanced" },
              ]}
              value={difficultyTo()}
              onChange={this.changeDifficultyTo}
            />
          </div>
        </Field>
        <Field label={`Length (${lengthUnit})`}>
          <div class="fieldRange">
            <Select
              aria-label="From"
              size={size}
              options={[
                { value: "0", label: "0" },
                { value: "1000", label: "1,000" },
                { value: "10000", label: "10,000" },
              ]}
              value={lengthFrom()}
              onChange={this.changeLengthFrom}
            />
            to
            <Select
              aria-label="To"
              size={size}
              options={[
                { value: "1000", label: "1,000" },
                { value: "10000", label: "10,000" },
                { value: "Infinity", label: "10,000+" },
              ]}
              value={lengthTo()}
              onChange={this.changeLengthTo}
            />
          </div>
        </Field>
        <Field label="Category">
          <Chips
            aria-label="Category"
            options={[
              { value: "All", label: "All" },
              ...bookUtils.categories.map((category) => ({
                value: category,
                label: bookUtils.categoryLabel(category),
              })),
            ]}
            selected={picked.length > 0 ? picked : ["All"]}
            onToggle={this.toggleCategory}
          />
        </Field>
        <div class="filterSwitches">
          <label class="switchField aiFilter">
            Include AI-generated texts
            <input
              type="checkbox"
              class="switch"
              checked={includeAI()}
              onChange={(event) =>
                saveFilter({ includeAI: event.currentTarget.checked })
              }
            />
          </label>
          <label class="switchField videoFilter">
            <span>
              <i class="icon-film"></i>Only texts with video
            </span>
            <input
              type="checkbox"
              class="switch"
              checked={videoOnly()}
              onChange={(event) =>
                saveFilter({ videoOnly: event.currentTarget.checked })
              }
            />
          </label>
          {hasAccount ? (
            <>
              <label class="switchField completedFilter">
                Include completed texts
                <input
                  type="checkbox"
                  class="switch"
                  checked={includeCompleted()}
                  onChange={(event) =>
                    saveFilter({
                      includeCompleted: event.currentTarget.checked,
                    })
                  }
                />
              </label>
              <label class="switchField likedFilter">
                <span>
                  <i class="icon-heart"></i>Only texts I've liked
                </span>
                <input
                  type="checkbox"
                  class="switch"
                  checked={likedOnly()}
                  onChange={(event) =>
                    saveFilter({ likedOnly: event.currentTarget.checked })
                  }
                />
              </label>
            </>
          ) : null}
        </div>
      </>
    );
  }
}

/** The filters on a phone: a sheet over the screen */
class LibraryFiltersSheet extends FiltersComponent {
  render() {
    return (
      <Sheet
        title="Filters"
        leading={{
          label: "Clear",
          onClick: resetAllFilters,
          disabled: numberOfFiltersSet() === 0,
        }}
      >
        <LibraryFilterFields size="large" />
      </Sheet>
    );
  }
}

interface Props {
  searchQuery: SearchQueryStore;
}

interface State {
  /** The filters' popover, under their button (not on phones) */
  showFilters: boolean;
}

class LibraryFiltersView extends FiltersComponent<Props, State> {
  state: State = { showFilters: false };
  private disposeViewportWatch?: () => void;

  componentDidMount() {
    super.componentDidMount();
    // On phones it's a sheet, so a popover left open from a wider window shuts
    this.disposeViewportWatch = effect(() => {
      if (deviceDetect.smallViewport.value) this.closeFilters();
    });
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.disposeViewportWatch?.();
  }

  private closeFilters = () => this.setState({ showFilters: false });

  private openFilters = () => {
    if (deviceDetect.smallViewport.value) {
      showSheet(<LibraryFiltersSheet />);
    } else {
      this.setState({ showFilters: !this.state.showFilters });
    }
  };

  render() {
    const numberOfFilters = numberOfFiltersSet();
    const small = deviceDetect.smallViewport.value;
    const { showFilters } = this.state;
    const badge =
      numberOfFilters > 0 ? <span class="badge">{numberOfFilters}</span> : null;

    return (
      <div class="newLibraryFilters">
        {/* The tab names the page: no heading */}
        <div class="searchSortAndFilters">
          <SearchView
            model={this.props.searchQuery}
            // "texts by title or author" doesn't fit on phones
            placeholder={small ? "Search" : "Search texts by title or author"}
          />
          <Select
            aria-label="Sort by"
            icon="icon-sort-by-attributes"
            options={[
              { value: "hotness", label: "Hot" },
              { value: "totalVotes", label: "Top" },
              { value: "firstShareDate", label: "New" },
            ]}
            value={stores.user.state.value.publicSortBooksBy}
            onChange={(value) => {
              stores.user.save({ publicSortBooksBy: value });
            }}
          />
          <Popover
            popoverClass="filters"
            role="dialog"
            aria-label="Filters"
            open={showFilters && !small}
            onClose={this.closeFilters}
            anchor={
              // Just its icon (and count) on phones, leaving room to search
              <Button
                class="filtersButton"
                icon="icon-filter"
                active={numberOfFilters > 0}
                chevron={small ? undefined : showFilters ? "up" : "down"}
                aria-label={small ? "Filters" : undefined}
                aria-expanded={!small && showFilters}
                onClick={this.openFilters}
              >
                {small ? (
                  badge
                ) : (
                  <>
                    Filters
                    {badge}
                  </>
                )}
              </Button>
            }
          >
            <LibraryFilterFields size="medium" />
            <div class="filtersFooter">
              <Button
                kind="plain"
                disabled={numberOfFilters === 0}
                onClick={() => {
                  resetAllFilters();
                  this.closeFilters();
                }}
              >
                Clear all
              </Button>
              <Button kind="primary" onClick={this.closeFilters}>
                Done
              </Button>
            </div>
          </Popover>
        </div>
      </div>
    );
  }
}

export default LibraryFiltersView;
